import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react/dist/iconify.js";
import { API_URL } from "..";

// Reusable Input Component
const FormInput = ({ label, name, type, value, onChange, error, required, placeholder }) => (
  <div className="mb-20">
    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
      {label} {required && <span className="text-danger-600">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="form-control radius-8"
      placeholder={placeholder}
    />
    {error && <small className="text-danger-600">{error}</small>}
  </div>
);

// Reusable Select Component
const FormSelect = ({ label, name, value, onChange, options, error, required }) => (
  <div className="mb-20">
    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
      {label} {required && <span className="text-danger-600">*</span>}
    </label>
    <select name={name} value={value} onChange={onChange} className="form-control radius-8">
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {error && <small className="text-danger-600">{error}</small>}
  </div>
);

const AddDriverPage = () => {
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    driving_license: "",
    license_expire_date: "",
    license_class: "",
    government_id: "",
    hire_date: "",
    job_title: "",
    employment_type: "",
    truck_id: "",
    salary_amount: "",
  });
  const [selectedPayoutMode, setSelectedPayoutMode] = useState("hourly");
  const [truckData, setTruckData] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [visitedSteps, setVisitedSteps] = useState([1]);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });

  // Fetch trucks
  const fetchTrucks = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const response = await fetch(`${API_URL}/api/truck/${user.id}`, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch trucks");
      const { truck } = await response.json();
      const availableTrucks = truck
        .filter((item) => !item.driver)
        .map((e) => ({
          label: e.license_plate ? `${e.name} - ${e.license_plate}` : e.name,
          value: e.id,
        }));
      setTruckData([{ value: "", label: "--SELECT TRUCK--" }, ...availableTrucks]);
    } catch (error) {
      console.error("Error fetching trucks:", error);
    }
  }, []);

  useEffect(() => {
    fetchTrucks();
  }, [fetchTrucks]);

  // Validation Logic
  const validateStep = useCallback((step) => {
    const newErrors = {};
    const requiredFields = {
      1: ["name", "email", "password", "phone"],
      2: ["driving_license", "license_expire_date"],
      3: ["hire_date", "job_title", "employment_type", "salary_amount", "truck_id"],
    }[step] || [];

    requiredFields.forEach((field) => {
      if (!formValues[field]) {
        newErrors[field] = `${field.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())} is required`;
      }
    });

    if (step === 1 && formValues.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
      newErrors.email = "Valid email is required";
    }
    if (step === 3 && formValues.salary_amount && Number(formValues.salary_amount) <= 0) {
      newErrors.salary_amount = "Salary must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formValues]);

  // Navigation Handlers
  const handleNext = () => {
    if (validateStep(currentStep)) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setVisitedSteps((prev) => [...new Set([...prev, nextStep])]);
      setErrors({});
    }
  };

  const handleBack = () => setCurrentStep((prev) => prev - 1);
  const handleStepClick = (step) => visitedSteps.includes(step) && setCurrentStep(step);

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    const user = JSON.parse(localStorage.getItem("user"));

    Object.entries(formValues).forEach(([key, value]) =>
      formData.append(key === "employment_type" ? "employement_type" : key, value || "")
    );
    formData.append("payout_type", selectedPayoutMode);
    formData.append("affiliate_company_id", user.id);

    ["medical_examination", "check_clearance", "drug_result", "training_certifications"].forEach((field) => {
      if (e.target[field]?.files[0]) formData.append(field, e.target[field].files[0]);
    });

    try {
      const response = await fetch(`${API_URL}/api/driver/create`, { method: "POST", body: formData });
      if (!response.ok) throw new Error("Failed to submit form");
      await response.json();
      setAlert({ show: true, type: "success", message: "Driver added successfully!" });
      setFormValues({ ...Object.fromEntries(Object.keys(formValues).map((k) => [k, ""])), salary_amount: "" });
      setCurrentStep(1);
      setVisitedSteps([1]);
    } catch (error) {
      setAlert({ show: true, type: "error", message: "Failed to submit form. Please try again." });
    }
  };

  // Step Line Component
  const StepLine = ({ currentStep, totalSteps = 4 }) => {
    const steps = [
      { id: 1, label: "Basic Info" },
      { id: 2, label: "License" },
      { id: 3, label: "Employment" },
      { id: 4, label: "Compliance" },
    ];

    return (
      <div style={{ marginBottom: "24px", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative" }}>
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <motion.div
                onClick={() => handleStepClick(step.id)}
                whileHover={visitedSteps.includes(step.id) ? { scale: 1.05 } : {}}
                style={{
                  cursor: visitedSteps.includes(step.id) ? "pointer" : "not-allowed",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  zIndex: 1,
                }}
              >
                <motion.div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    backgroundColor: step.id <= currentStep ? "#007bff" : "#d3d3d3",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                  }}
                  animate={{ scale: step.id === currentStep ? 1.15 : 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {step.id}
                </motion.div>
                <span style={{ fontSize: "12px", marginTop: "6px" }}>{step.label}</span>
              </motion.div>
              {index < steps.length - 1 && (
                <motion.div
                  style={{
                    height: "2px",
                    width: "50px",
                    backgroundColor: step.id < currentStep ? "#007bff" : "#d3d3d3",
                    position: "relative",
                    top: "-16px",
                  }}
                  initial={{ backgroundColor: "#d3d3d3" }}
                  animate={{ backgroundColor: step.id < currentStep ? "#007bff" : "#d3d3d3" }}
                  transition={{ duration: 0.5 }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Animation Variants
  const stepVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 },
  };

  const renderStep = () => {
    const stepsContent = {
      1: (
        <>
          <h6 className="mb-20">Basic Information</h6>
          <FormInput
            label="Full Name"
            name="name"
            type="text"
            value={formValues.name}
            onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
            error={errors.name}
            required
            placeholder="Enter Full Name"
          />
          <FormInput
            label="Email"
            name="email"
            type="email"
            value={formValues.email}
            onChange={(e) => setFormValues((prev) => ({ ...prev, email: e.target.value }))}
            error={errors.email}
            required
            placeholder="Enter Email"
          />
          <FormInput
            label="Phone"
            name="phone"
            type="tel"
            value={formValues.phone}
            onChange={(e) => setFormValues((prev) => ({ ...prev, phone: e.target.value }))}
            error={errors.phone}
            required
            placeholder="Enter Phone"
          />
          <FormInput
            label="Password"
            name="password"
            type="password"
            value={formValues.password}
            onChange={(e) => setFormValues((prev) => ({ ...prev, password: e.target.value }))}
            error={errors.password}
            required
            placeholder="Enter Password"
          />
          <div className="d-flex justify-content-center">
            <button
              type="button"
              onClick={handleNext}
              className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
            >
              Next
            </button>
          </div>
        </>
      ),
      2: (
        <>
          <h6 className="mb-20">Identification & Licensing</h6>
          <FormInput
            label="Driver’s License Number"
            name="driving_license"
            type="text"
            value={formValues.driving_license}
            onChange={(e) => setFormValues((prev) => ({ ...prev, driving_license: e.target.value }))}
            error={errors.driving_license}
            required
            placeholder="Enter License Number"
          />
          <FormInput
            label="License Expiry Date"
            name="license_expire_date"
            type="date"
            value={formValues.license_expire_date}
            onChange={(e) => setFormValues((prev) => ({ ...prev, license_expire_date: e.target.value }))}
            error={errors.license_expire_date}
            required
          />
          <FormInput
            label="License Class/Type (CDL, Non-CDL)"
            name="license_class"
            type="text"
            value={formValues.license_class}
            onChange={(e) => setFormValues((prev) => ({ ...prev, license_class: e.target.value }))}
            error={errors.license_class}
            placeholder="Enter License Class"
          />
          <FormInput
            label="Government ID"
            name="government_id"
            type="text"
            value={formValues.government_id}
            onChange={(e) => setFormValues((prev) => ({ ...prev, government_id: e.target.value }))}
            error={errors.government_id}
            placeholder="Enter Government ID"
          />
          <div className="d-flex justify-content-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="btn btn-outline-primary text-md px-56 py-12 radius-8"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
            >
              Next
            </button>
          </div>
        </>
      ),
      3: (
        <>
          <h6 className="mb-20">Employment Details</h6>
          <FormInput
            label="Hire Date"
            name="hire_date"
            type="date"
            value={formValues.hire_date}
            onChange={(e) => setFormValues((prev) => ({ ...prev, hire_date: e.target.value }))}
            error={errors.hire_date}
            required
          />
          <FormInput
            label="Job Title"
            name="job_title"
            type="text"
            value={formValues.job_title}
            onChange={(e) => setFormValues((prev) => ({ ...prev, job_title: e.target.value }))}
            error={errors.job_title}
            required
            placeholder="Enter Job Title"
          />
          <FormSelect
            label="Employment Type"
            name="employment_type"
            value={formValues.employment_type}
            onChange={(e) => setFormValues((prev) => ({ ...prev, employment_type: e.target.value }))}
            options={[
              { value: "", label: "--SELECT TYPE--" },
              { value: "Full-time", label: "Full-time" },
              { value: "Part-time", label: "Part-time" },
              { value: "Contractor", label: "Contractor" },
            ]}
            error={errors.employment_type}
            required
          />
          <div className="mb-20">
            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
              Payout Mode <span className="text-danger-600">*</span>
            </label>
            <div className="d-flex gap-3">
              {["hourly", "weekly", "monthly"].map((mode) => (
                <div key={mode} className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payout_mode"
                    value={mode}
                    checked={selectedPayoutMode === mode}
                    onChange={(e) => setSelectedPayoutMode(e.target.value)}
                  />
                  <label className="form-check-label">{mode.charAt(0).toUpperCase() + mode.slice(1)}</label>
                </div>
              ))}
            </div>
          </div>
          <FormInput
            label={`${selectedPayoutMode.charAt(0).toUpperCase() + selectedPayoutMode.slice(1)} Salary`}
            name="salary_amount"
            type="number"
            value={formValues.salary_amount}
            onChange={(e) => setFormValues((prev) => ({ ...prev, salary_amount: e.target.value }))}
            error={errors.salary_amount}
            required
            placeholder="0.00"
          />
          <FormSelect
            label="Assigned Vehicle"
            name="truck_id"
            value={formValues.truck_id}
            onChange={(e) => setFormValues((prev) => ({ ...prev, truck_id: e.target.value }))}
            options={truckData}
            error={errors.truck_id}
            required
          />
          <div className="d-flex justify-content-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="btn btn-outline-primary text-md px-56 py-12 radius-8"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
            >
              Next
            </button>
          </div>
        </>
      ),
      4: (
        <>
          <h6 className="mb-20">Compliance & Certifications</h6>
          {["Medical Examination", "Check Clearance", "Drug Result", "Training Certifications"].map(
            (label, idx) => (
              <div key={label} className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">{label}</label>
                <input
                  type="file"
                  name={["medical_examination", "check_clearance", "drug_result", "training_certifications"][idx]}
                  className="form-control radius-8"
                />
              </div>
            )
          )}
          <div className="d-flex justify-content-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="btn btn-outline-primary text-md px-56 py-12 radius-8"
            >
              Back
            </button>
            <button
              type="submit"
              className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
            >
              Submit
            </button>
          </div>
        </>
      ),
    };

    return (
      <motion.div
        key={currentStep}
        variants={stepVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.3 }}
      >
        {stepsContent[currentStep]}
      </motion.div>
    );
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Add Driver" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <div className="row justify-content-center">
            <div className="col-xxl-6 col-xl-8 col-lg-10">
              <div className="card border">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <StepLine currentStep={currentStep} />
                    <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
                    {alert.show && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className={`alert alert-${alert.type} bg-${alert.type}-50 text-${alert.type}-600 border-${alert.type}-50 px-24 py-11 mb-10 fw-semibold text-lg radius-8 d-flex align-items-center justify-content-between`}
                      >
                        {alert.message}
                        <button
                          type="button"
                          onClick={() => setAlert({ show: false, type: "", message: "" })}
                          className="text-secondary-600 text-xxl"
                        >
                          <Icon icon="iconamoon:sign-times-light" />
                        </button>
                      </motion.div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default AddDriverPage;